import React, {
  useState,
} from "react";

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Link,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";

import {
  httpsCallable,
} from "firebase/functions";

import {
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import {
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";

import {
  auth,
  functions,
} from "../firebase/config";

const RegisterForm = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [displayName, setDisplayName] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [loading, setLoading] =
    useState(false);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const normalizedEmail = email
      .trim()
      .toLowerCase();

    if (!displayName.trim()) {
      setErrorMessage(
        "Please enter your full name."
      );

      return;
    }

    if (!normalizedEmail) {
      setErrorMessage(
        "Please enter your email address."
      );

      return;
    }

    if (password.length < 8) {
      setErrorMessage(
        "Your password must contain at least 8 characters."
      );

      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(
        "The passwords do not match."
      );

      return;
    }

    setLoading(true);

    let accountCreated = false;

    try {
      const registerUser = httpsCallable(
        functions,
        "registerUser"
      );

      await registerUser({
        displayName:
          displayName.trim(),
        email: normalizedEmail,
        password,
      });

      accountCreated = true;

      /*
       * Prevent the global session listener from treating
       * this temporary sign-in as application access.
       */
      sessionStorage.setItem(
        "registrationVerificationInProgress",
        "true"
      );

      try {
        const userCredential =
          await signInWithEmailAndPassword(
            auth,
            normalizedEmail,
            password
          );

        await sendEmailVerification(
          userCredential.user,
          {
            url:
              `${window.location.origin}` +
              "/login",
            handleCodeInApp: false,
          }
        );
      } finally {
        if (auth.currentUser) {
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error(
              "Registration sign-out failed:",
              {
                code:
                  signOutError.code ||
                  "unknown",
              }
            );
          }
        }

        sessionStorage.removeItem(
          "registrationVerificationInProgress"
        );
      }

      toast({
        title: "Check your email",
        description:
          "Open the verification link we sent you. After verification, an administrator must approve your account.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      navigate("/login", {
        replace: true,
        state: {
          verificationEmail:
            normalizedEmail,
        },
      });
    } catch (error) {
      console.error(
        "Registration or verification failed:",
        {
          code:
            error.code || "unknown",
        }
      );

      sessionStorage.removeItem(
        "registrationVerificationInProgress"
      );

      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error(
            "Registration cleanup failed:",
            {
              code:
                signOutError.code ||
                "unknown",
            }
          );
        }
      }

      let message =
        "Registration could not be completed. Please try again.";

      if (accountCreated) {
        message =
          "Your account was created, but the verification email could not be sent. Go to the sign-in page to request another verification email.";
      } else if (
        error.code ===
        "functions/already-exists"
      ) {
        message =
          "An account already exists for this email address. Sign in or reset your password.";
      } else if (
        error.code ===
        "functions/invalid-argument"
      ) {
        message =
          "Please check the information you entered and try again.";
      } else if (
        error.code ===
          "functions/resource-exhausted" ||
        error.code ===
          "auth/too-many-requests"
      ) {
        message =
          "Too many attempts were made. Please wait and try again.";
      }

      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      bg="white"
      maxW="800px"
      mx="auto"
      mt={10}
      p={{
        base: 5,
        md: 6,
      }}
      borderWidth={1}
      borderRadius="lg"
      shadow="sm"
    >
      <Flex
        align="center"
        mb={6}
      >
        <Image
          src="/assets/logo.png"
          alt="Capital Project"
          width="300px"
          maxW="100%"
        />
      </Flex>

      <Text
        fontSize="2xl"
        fontWeight="bold"
        mb={2}
      >
        Create Your Account
      </Text>

      <Text
        color="gray.600"
        mb={6}
      >
        After registering, verify your email
        address. An administrator will then
        review your account request.
      </Text>

      <form onSubmit={handleSubmit}>
        <VStack spacing={4}>
          {errorMessage && (
            <Alert status="error">
              <AlertIcon />

              {errorMessage}
            </Alert>
          )}

          <FormControl isRequired>
            <FormLabel>
              Full Name
            </FormLabel>

            <Input
              value={displayName}
              onChange={(event) =>
                setDisplayName(
                  event.target.value
                )
              }
              autoComplete="name"
              maxLength={100}
              disabled={loading}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>
              Email
            </FormLabel>

            <Input
              type="email"
              value={email}
              onChange={(event) =>
                setEmail(
                  event.target.value
                )
              }
              autoComplete="email"
              maxLength={254}
              disabled={loading}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>
              Password
            </FormLabel>

            <Input
              type="password"
              value={password}
              onChange={(event) =>
                setPassword(
                  event.target.value
                )
              }
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              disabled={loading}
            />

            <Text
              mt={1}
              fontSize="sm"
              color="gray.600"
            >
              Use at least 8 characters.
            </Text>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>
              Confirm Password
            </FormLabel>

            <Input
              type="password"
              value={confirmPassword}
              onChange={(event) =>
                setConfirmPassword(
                  event.target.value
                )
              }
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              disabled={loading}
            />
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading}
            loadingText="Creating Account"
            width="full"
          >
            Create Account
          </Button>

          <Text fontSize="sm">
            Already have an account?{" "}
            <Link
              as={RouterLink}
              to="/login"
              color="blue.600"
              fontWeight="semibold"
            >
              Sign in
            </Link>
          </Text>
        </VStack>
      </form>
    </Box>
  );
};

export default RegisterForm;